import { useState } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Button } from '../../components/ui/button';
import { 
  BarChart3, Users, Shield, ChevronRight, ChevronLeft, 
  Target, FileText, Eye, Briefcase, CheckCircle 
} from 'lucide-react';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

const steps = [
  {
    id: 'discover',
    badge: 'Stap 1 van 3',
    title: 'Ontdek passende klantkansen',
    subtitle: 'Met Spooker ziet u welke klantbehoeften en risico\'s aansluiten op uw diensten.',
    features: [
      { icon: Target, title: 'Passende kansen', desc: 'Zie welke klantvragen en risico\'s aansluiten op uw expertise.' },
      { icon: FileText, title: 'Trajecten volgen', desc: 'Beheer lopende contacten, aanvragen en offertes.' },
      { icon: Eye, title: 'Zichtbaar worden', desc: 'Alleen leveranciers met een goedgekeurd profiel worden meegenomen in matching en advies.' },
    ],
    accent: 'from-blue-600 to-cyan-500',
  },
  {
    id: 'packages',
    badge: 'Stap 2 van 3',
    title: 'Kies uw manier van opvolgen',
    subtitle: 'Uw pakket bepaalt hoe u kansen ontvangt en opvolgt. Uw pakket heeft geen invloed op top 3-advies aan klanten.',
    features: [
      { icon: Users, title: 'Zelf contact leggen', desc: 'U ziet passende kansen en neemt zelf contact op met klanten.' },
      { icon: Briefcase, title: 'Gekwalificeerde signalen', desc: 'U wordt geïnformeerd over relevante potentiële klanten met een sterke match.' },
      { icon: Shield, title: 'Begeleide aanvragen', desc: 'Spooker vertaalt klantwensen naar een offerteverzoek waarop u gericht kunt reageren.' },
    ],
    notice: 'Aanbevelingen aan klanten worden bepaald op basis van expertise en dienstverlening uit uw profiel, niet op basis van uw pakket.',
    accent: 'from-amber-500 to-orange-500',
  },
  {
    id: 'profile',
    badge: 'Stap 3 van 3',
    title: 'Maak uw profiel zichtbaar',
    subtitle: 'Uw profiel bepaalt of en hoe u zichtbaar wordt voor klanten. We controleren elk profiel voordat het live gaat.',
    checklist: [
      'Bedrijfsinformatie',
      'Diensten en expertises',
      'Wat u wel en niet doet (afbakening)',
      'Certificeringen en onderbouwing',
      'Korte presentatie voor klanten',
    ],
    reviewPoints: [
      'Is uw profiel specifiek genoeg',
      'Zijn uw claims realistisch',
      'Past uw dienstverlening bij wat u aanbiedt',
      'Is duidelijk wat buiten uw scope valt',
    ],
    accent: 'from-green-500 to-emerald-500',
  },
];

const SupplierOnboarding = ({ onComplete }) => {
  const { api } = useSupplierAuth();
  const [current, setCurrent] = useState(0);
  const [completing, setCompleting] = useState(false);
  const step = steps[current];

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post('/profile/complete-onboarding');
    } catch (e) {
      console.error('Onboarding completion error:', e);
    }
    setCompleting(false);
    onComplete?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="supplier-onboarding">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-neutral-100">
        <img src={SPOOKER_LOGO} alt="Spooker" className="h-5" />
        <div className="flex items-center gap-2">
          {steps.map((_, i) => (
            <div key={`step-${i}`} className={`h-1.5 rounded-full transition-all ${i === current ? 'w-8 bg-black' : i < current ? 'w-4 bg-green-400' : 'w-4 bg-neutral-200'}`} />
          ))}
        </div>
        <span className="text-xs text-neutral-400">{step.badge}</span>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full space-y-8">
          {/* Title */}
          <div className="text-center space-y-3">
            <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br ${step.accent} mx-auto`}>
              {current === 0 && <BarChart3 className="h-7 w-7 text-white" strokeWidth={1.5} />}
              {current === 1 && <Users className="h-7 w-7 text-white" strokeWidth={1.5} />}
              {current === 2 && <Shield className="h-7 w-7 text-white" strokeWidth={1.5} />}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-black">{step.title}</h1>
            <p className="text-neutral-500 text-base max-w-lg mx-auto">{step.subtitle}</p>
          </div>

          {/* Features (steps 1 & 2) */}
          {step.features && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {step.features.map((f, i) => (
                <div key={`feature-${f.title}`} className="p-5 rounded-xl border border-neutral-100 bg-neutral-50/50 space-y-3" data-testid={`onboarding-feature-${i}`}>
                  <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${step.accent} flex items-center justify-center`}>
                    <f.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm font-bold text-black">{f.title}</p>
                  <p className="text-xs text-neutral-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          )}

          {/* Notice (step 2) */}
          {step.notice && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-center">
              <p className="text-xs text-amber-700">{step.notice}</p>
            </div>
          )}

          {/* Checklist (step 3) */}
          {step.checklist && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Wat u invult</p>
                {step.checklist.map((item) => (
                  <div key={item} className="flex items-center gap-2.5 text-sm text-neutral-700">
                    <CheckCircle className="h-4 w-4 text-green-500 shrink-0" strokeWidth={1.5} />
                    {item}
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Waar we op letten</p>
                {step.reviewPoints.map((item) => (
                  <div key={item} className="flex items-center gap-2.5 text-sm text-neutral-700">
                    <Eye className="h-4 w-4 text-blue-500 shrink-0" strokeWidth={1.5} />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-8 py-5 border-t border-neutral-100">
        <Button
          variant="ghost"
          size="sm"
          disabled={current === 0}
          onClick={() => setCurrent(c => c - 1)}
          data-testid="onboarding-prev"
        >
          <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Vorige
        </Button>

        {current < steps.length - 1 ? (
          <Button
            size="sm"
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => setCurrent(c => c + 1)}
            data-testid="onboarding-next"
          >
            Volgende <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button
            size="sm"
            className="bg-black text-white hover:bg-neutral-800"
            onClick={handleComplete}
            disabled={completing}
            data-testid="onboarding-start-profile"
          >
            Start met uw profiel <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default SupplierOnboarding;
